// Settings helpers games compose their own `settings` object from: a
// nickname sanitiser, a rebindable-keys store and a zoom store, all
// persisted through the platform layer. See docs/screens/menu.md.
import { NAME_MAX } from '@platoon/netcore';
import type { Platform } from './platform';

// Trim, drop control characters, cap at `maxBytes` of utf-8 (cutting on a
// code point boundary). Empty result means "no nickname set".
export function sanitizeNickname(raw: string, maxBytes: number = NAME_MAX): string {
  let s = '';
  for (const ch of raw.trim()) {
    const c = ch.codePointAt(0)!;
    if (c < 0x20 || c === 0x7f) continue;
    s += ch;
  }
  const enc = new TextEncoder();
  while (s.length > 0 && enc.encode(s).length > maxBytes) s = Array.from(s).slice(0, -1).join('');
  return s;
}

export interface Binding {
  code: number; // KeyboardEvent.keyCode / Phaser.Input.Keyboard.KeyCodes
  label: string;
}

export interface ActionInfo<A extends string> {
  action: A;
  name: string;
  help: string;
}

export interface BindingStore<A extends string> {
  readonly bindings: Readonly<Record<A, Binding>>;
  // Bind `action` to a key. If another action already uses that key the two
  // swap, so no action is left without a key. False for a reserved key.
  set(action: A, binding: Binding): boolean;
  reset(): void;
}

export function createBindingStore<A extends string>(
  platform: Platform,
  storageKey: string,
  actions: ReadonlyArray<ActionInfo<A>>,
  defaults: Readonly<Record<A, Binding>>,
  reserved: ReadonlySet<number>,
): BindingStore<A> {
  let bindings: Record<A, Binding> = { ...defaults };
  const raw = platform.loadSetting(storageKey);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<Record<A, Binding>>;
      for (const { action } of actions) {
        const b = parsed[action];
        if (b && Number.isInteger(b.code) && typeof b.label === 'string' && !reserved.has(b.code)) {
          bindings[action] = { code: b.code, label: b.label.slice(0, 12) };
        }
      }
    } catch {
      /* corrupt value: defaults */
    }
  }
  return {
    get bindings() {
      return bindings;
    },
    set(action, binding) {
      if (reserved.has(binding.code)) return false;
      for (const { action: other } of actions) {
        if (other !== action && bindings[other].code === binding.code) bindings[other] = bindings[action];
      }
      bindings[action] = { code: binding.code, label: binding.label.slice(0, 12) };
      platform.saveSetting(storageKey, JSON.stringify(bindings));
      return true;
    },
    reset() {
      bindings = { ...defaults };
      platform.saveSetting(storageKey, '');
    },
  };
}

export interface ZoomStore {
  readonly value: number;
  set(z: number, min?: number): number; // clamped to [min, max]; 1 = default
}

export function createZoomStore(platform: Platform, storageKey: string, minAbs: number, max: number): ZoomStore {
  const saved = Number(platform.loadSetting(storageKey));
  let value = Number.isFinite(saved) && saved >= minAbs && saved <= max ? saved : 1;
  return {
    get value() {
      return value;
    },
    set(z, min = minAbs) {
      value = Math.min(max, Math.max(min, z));
      platform.saveSetting(storageKey, value === 1 ? '' : value.toFixed(3));
      return value;
    },
  };
}

// A readable label for a key: "W", "Shift", "Space", "←", "Numpad 1".
export function keyLabel(ev: KeyboardEvent): string {
  const k = ev.key;
  if (k === ' ') return 'Space';
  if (k === 'ArrowUp') return '↑';
  if (k === 'ArrowDown') return '↓';
  if (k === 'ArrowLeft') return '←';
  if (k === 'ArrowRight') return '→';
  if (ev.code.startsWith('Numpad')) return `Numpad ${ev.code.slice(6)}`;
  return k.length === 1 ? k.toUpperCase() : k;
}
